import React from 'react'

import { isEmpty } from 'ramda'
import { GroupBase, DropdownIndicatorProps, components } from 'react-select'
import styled from 'styled-components'

import type { SelectOptionInterface, IconNotebook } from '@customTypes'
import { Icon } from '_components/icon'
import { palette } from '_styles/variables'

type IndicatorProps<
  Option extends SelectOptionInterface = SelectOptionInterface,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>
> = Omit<DropdownIndicatorProps<Option, IsMulti, Group>, 'cx'>

const StyledIcon = styled(Icon)`
  margin-left: 0.25rem;
  margin-right: 0.5rem;
  color: ${palette.neutral['grey-icons']};
  pointer-events: none;
`

export const getDropdownIndicator = <
  Option extends SelectOptionInterface,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>({
  config,
  disableWhenHasValue,
  hideDropdownIcon = false,
}: {
  config: {
    iconClosed: IconNotebook
    iconOpen?: IconNotebook
  }
  disableWhenHasValue?: boolean
  hideDropdownIcon?: boolean
}) => {
  const DropdownIndicator = React.memo(
    ({ selectProps, getValue, className }: IndicatorProps<Option, IsMulti, Group>) => {
      if (hideDropdownIcon) return null

      const closed = config.iconClosed || 'chevron-down'
      const hasValue = getValue().some(val => !isEmpty(val))
      const open = config.iconOpen || config.iconClosed || 'chevron-up'

      const icon: IconNotebook = selectProps.menuIsOpen ? open : closed

      if (hasValue && disableWhenHasValue) {
        return null
      }

      return <StyledIcon className={className} icon={icon} size="1.5em" aria-label={selectProps.menuIsOpen ? "Close select" : "Open select"} />
    }
  )

  DropdownIndicator.displayName = 'DropdownIndicator'

  return DropdownIndicator as typeof components.DropdownIndicator
}
